// A line naming another ouvrage comes with that ouvrage's cascade (CascadeTree),
// which holds its sub-ouvrages then its lines: the order a quote receives them in.
import React, { useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import IconBadge  from "@react/shared/ui/IconBadge"
import IconButton from "@react/shared/ui/IconButton"
import { TreeLead, TreeStem, trailBelow } from "@react/shared/ui/TreeGuides"
import { costCategoryVisual, findCostCategory, OUVRAGE_VISUAL } from "../kinds"

const leafOf = line => ({
  ouvrage:        false,
  cost_category:  line.cost_category,
  ref:            line.ref_product_distrib || "",
  description:    line.description || "",
  quantity:       parseFloat(line.quantity) || 0,
  unit:           line.unit || "",
  unit_price:     parseFloat(line.unit_price) || 0,
  purchase_price: parseFloat(line.purchase_price) || 0,
  children:       [],
})

export const ouvrageOf = (node, quantity = node.quantity) => ({
  ouvrage:        true,
  ref:            node.ref_product_distrib || "",
  description:    node.name || "",
  quantity:       parseFloat(quantity) || 0,
  unit:           node.unit || "",
  unit_price:     parseFloat(node.unit_price) || 0,
  purchase_price: parseFloat(node.purchase_price) || 0,
  children:       [...(node.children || []).map(child => ouvrageOf(child)), ...(node.lines || []).map(leafOf)],
})

export const linesOfDetail = detail => {
  const cascades = new Map((detail?.children || []).map(child => [child.id, child]))
  return (detail?.qline_breakdown_blueprints || []).map(line => {
    const cascade = line.child_blueprint && cascades.get(line.child_blueprint.id)
    return cascade ? ouvrageOf(cascade, line.quantity) : leafOf(line)
  })
}

// A row's total is scaled by the quantities above it, so each level adds up to its parent.
const TreeRow = ({ node, depth, trail, last, factor, columns, span, indent, renderFigures }) => {
  const [open, setOpen] = useState(true)
  const composed = node.ouvrage && node.children.length > 0
  const visual   = node.ouvrage ? OUVRAGE_VISUAL : costCategoryVisual(node.cost_category)
  const label    = node.description || findCostCategory(node.cost_category)?.label || "—"
  const total    = factor * node.quantity * node.unit_price

  const toggle = (
    <IconButton
      size      = "xs"
      onClick   = {() => setOpen(current => !current)}
      title     = {I18n.t("quotes.builder.aggregate.toggle_collapse")}
      className = {`shrink-0 [&_svg]:transition-transform [&_svg]:text-[10px] ${open ? "" : "[&_svg]:-rotate-90"}`}
    >
      <FontAwesomeIcon icon="chevron-down" />
    </IconButton>
  )

  return (
    <>
      <div className="grid items-stretch gap-x-2 rounded-md hover:bg-white" style={{ gridTemplateColumns: columns }}>
        <div className="flex items-stretch min-w-0" style={{ gridColumn: `span ${span} / span ${span}`, paddingLeft: indent }}>
          <TreeLead depth={depth} trail={trail} last={last} toggle={composed ? toggle : null} />
          <span className="relative shrink-0 flex items-center">
            <IconBadge
              tone  = {visual.tone}
              size  = "sm"
              title = {node.ouvrage ? I18n.t("quotes.qline_service_blueprints.form.component_chip") : findCostCategory(node.cost_category)?.label}
            >
              <FontAwesomeIcon icon={visual.icon} />
            </IconBadge>
            {composed && open && <TreeStem />}
          </span>
          {/* The reference after the label, as in the library's lists: the labels of a level line up. */}
          <div className="flex-1 min-w-0 flex items-center pl-2.5 py-1.5">
            <span className={`min-w-0 text-[12.5px] leading-[1.35] ${composed ? "font-semibold text-slate-900" : "text-slate-700"}`}>
              {label}
              {node.ref && <span className="ml-1.5 font-mono text-[11px] font-medium text-slate-400">{node.ref}</span>}
            </span>
          </div>
        </div>
        {renderFigures(node, total)}
      </div>

      {composed && open && (
        <BlueprintTree
          nodes         = {node.children}
          depth         = {depth + 1}
          trail         = {trailBelow(depth, trail, last)}
          factor        = {factor * node.quantity}
          columns       = {columns}
          span          = {span}
          indent        = {indent}
          renderFigures = {renderFigures}
        />
      )}
    </>
  )
}

// Each row is a grid of `columns`: the tree over the first `span`, then `renderFigures(node, total)`.
const BlueprintTree = ({ nodes, depth = 0, trail = [], factor = 1, columns, span = 1, indent = 0, renderFigures }) => (
  <>
    {nodes.map((node, index) => (
      <TreeRow
        key           = {index}
        node          = {node}
        depth         = {depth}
        trail         = {trail}
        last          = {index === nodes.length - 1}
        factor        = {factor}
        columns       = {columns}
        span          = {span}
        indent        = {indent}
        renderFigures = {renderFigures}
      />
    ))}
  </>
)

export default BlueprintTree
